import RAPIER from '@dimforge/rapier3d-compat';
import { CONTAINER, PHYSICS } from '../config';

export class PhysicsWorld {
  world!: RAPIER.World;
  eventQueue!: RAPIER.EventQueue;
  private ready = false;

  async init(): Promise<void> {
    await RAPIER.init();
    const gravity = { x: 0.0, y: PHYSICS.gravity, z: 0.0 };
    this.world = new RAPIER.World(gravity);
    this.world.timestep = PHYSICS.timeStep;
    // Slightly tighter solver for stack stability
    this.world.integrationParameters.numSolverIterations = 8;
    this.eventQueue = new RAPIER.EventQueue(true);
    this.buildContainer();
    this.ready = true;
  }

  get isReady(): boolean {
    return this.ready;
  }

  private buildContainer(): void {
    const w = CONTAINER.width;
    const h = CONTAINER.height;
    const t = CONTAINER.wallThickness;
    const ft = CONTAINER.floorThickness;
    const halfW = w / 2;
    const halfH = h / 2;

    const staticBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());

    const makeWall = (x: number, y: number, hw: number, hh: number, friction: number) => {
      const coll = RAPIER.ColliderDesc.cuboid(hw, hh, 1)
        .setTranslation(x, y, 0)
        .setFriction(friction)
        .setRestitution(CONTAINER.restitution)
        .setCollisionGroups(0x00010001);
      this.world.createCollider(coll, staticBody);
    };

    // Floor
    makeWall(0, -halfH - ft / 2, halfW + t, ft / 2, CONTAINER.floorFriction);
    // Left wall
    makeWall(-halfW - t / 2, 0, t / 2, halfH + ft, CONTAINER.wallFriction);
    // Right wall
    makeWall(halfW + t / 2, 0, t / 2, halfH + ft, CONTAINER.wallFriction);
    // Thin back plane to reduce Z drift (already locked)
    const back = RAPIER.ColliderDesc.cuboid(halfW + t, halfH + ft, 0.05)
      .setTranslation(0, 0, -0.6)
      .setFriction(0.1)
      .setRestitution(0)
      .setCollisionGroups(0x00020002);
    this.world.createCollider(back, staticBody);
  }

  step(dt: number): void {
    // Fixed step with clamp for tab-switch recovery
    const steps = Math.min(PHYSICS.maxSubsteps, Math.max(1, Math.round(dt / PHYSICS.timeStep)));
    for (let i = 0; i < steps; i++) {
      this.world.step(this.eventQueue);
    }
  }

  drainCollisionPairs(): Array<[number, number]> {
    const pairs: Array<[number, number]> = [];
    this.eventQueue.drainCollisionEvents((h1, h2, started) => {
      if (!started) return;
      pairs.push([h1, h2]);
    });
    return pairs;
  }

  dispose(): void {
    if (this.world) {
      this.world.free();
    }
    this.ready = false;
  }
}
